import React, {useEffect, useRef, useState} from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import clsx from 'clsx';
import {useTranslation} from "react-i18next";

import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SettingsIcon from '@mui/icons-material/Settings';

import ClearAllFiltersBtn from "./ClearAllFiltersBtn";
import ProfileMenu from './ProfileMenu';
import SearchBox from '../construct/components/SearchBox';

import {
    appSetAlert
    , appSetActivePageTitle
    , appSetLeftDrawerOpen
    , appSetRightDrawerOpen
    , appSetToolbarHeight
    , appSetModalContent
} from "../../redux/app/appActions";
import useDataSource from "../../hooks/useDataSource";

import {
    authLogin
    ,authGetAvatar
} from "../../redux/auth/authActions";

import {
    persistSettingsDrawersOpen
} from "../../redux/persist/persistActions";

import { axiosBaseURL } from '../../config/config';

import { styled, AppBar, Avatar, Toolbar, Typography, Button, IconButton, Tooltip } from '@mui/material';

const PREFIX = 'AppBar';

const classes = {
    root: `${PREFIX}-root`,
    appBar: `${PREFIX}-appBar`,
    menuButton: `${PREFIX}-menuButton`,
    settingsMenuButton: `${PREFIX}-settingsMenuButton`,
    hide: `${PREFIX}-hide`,
    title: `${PREFIX}-title`,
    pageName: `${PREFIX}-pageName`,
    widgets: `${PREFIX}-widgets`,
    searchBox: `${PREFIX}-searchBox`,
    avatarButton: `${PREFIX}-avatarButton`,
    smallAvatar: `${PREFIX}-smallAvatar`,
    headline: `${PREFIX}-headline`
};

const StyledAppBar = styled(AppBar)((
    {
        theme
    }
) => ({
    [`& .${classes.root}`]: {
        display: 'flex'
    },

    [`&.${classes.appBar}`]: {
        zIndex: theme.zIndex.drawer + 1,
    },

    [`& .${classes.menuButton}`]: {
        marginRight: theme.spacing(2),
        // display: 'block !important'
    },

    [`& .${classes.settingsMenuButton}`]: {
    //     height: '64px'
    },

    [`& .${classes.hide}`]: {
        display: 'none',
    },

    [`& .${classes.title}`]: {
        textAlign: 'left',
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'block',
        },
    },

    [`& .${classes.pageName}`]: {
        flexGrow: 1,
        marginLeft: '16px',
        textAlign: 'center'
        // display: 'none',
        // [theme.breakpoints.up('md')]: {
        //     display: 'block'
        // }
    },

    [`& .${classes.widgets}`]: {
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: 'auto'
    },

    [`& .${classes.searchBox}`]: {
        maxWidth: '500px',
        marginLeft: '16px',
        marginRight: '16px',
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },

    [`& .${classes.avatarButton}`]: {
        padding: "8px"
    },

    [`& .${classes.smallAvatar}`]: {
        width: theme.spacing(4),
        height: theme.spacing(4)
    },

    [`& .${classes.headline}`]: {
      marginRight: '32px'
    }
}));

export default function ApplicationAppBar() {

    const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
    const app = useSelector(state => state.app);
    const auth = useSelector(state => state.auth);
    const config = useSelector(state => state.config.data);
    const user = useSelector(state => state.user);
    const persist = useSelector(state => state.persist);
    const viewStaged = useSelector(state => state.data.viewStaged);

    const settingsDrawersOpen = persist.settingsDrawersOpen;
    const isThemeLight = persist.themeLight;

    // Component State
    const [homePageHeadline, setHomePageHeadline] = useState("");
    const [searchEnabled, setSearchEnabled] = useState(false);
    const [searchConfig, setSearchConfig] = useState(null);

	// Get AppBar DataSource
	const appBarConfig = config?.global !== undefined ? config.global.find(container => container.name === "appbar").configuration : null;
	const dataSource = useDataSource(appBarConfig ? appBarConfig.dataSource.name : "");

    // Get active location of app
    let location = useLocation();
    let { id, tab, list } = useParams();

    const toolbar = useRef(null);

    // Set Active Page Title
    useEffect(() => {
        let params = [];

        if (id !== undefined) {
            params[0] = id;
            if (tab !== undefined) {
                params[1] = tab;
            }
        } else if (tab !== undefined) {
            params[0] = tab; // Not yet used, but if we pass Tab params alone, this will work
        } else if (list !== undefined) {
            params[0] = list;
        }
        dispatch(appSetActivePageTitle(params));
    }, []);

    // Path Change
    useEffect(() => {
		// Clear out existing alert
        dispatch(appSetAlert(null, null, null, null));
    }, [location.pathname]);

    // Features
    useEffect(() => {
        if (user.roleFeatures) {

            const roleFeatures = user.roleFeatures;
            const searchComponentFeature = roleFeatures.filter(feature => feature.featureType === 51);

            if (searchComponentFeature && searchComponentFeature.length > 0) {
                setSearchEnabled(true);
                setSearchConfig(searchComponentFeature);
            }
        }

    }, [user.roleFeatures]);

    // Loaded
    useEffect(() => {
		if (dataSource.data && dataSource.fetched) {
			const appBarHLField = appBarConfig.dataSource.fields.headline;
			setHomePageHeadline(dataSource.data[0][appBarHLField]);
		}
    }, [dataSource.fetched]);

    // App & Persist Settings Drawer Open/Closed
    useEffect( () => {
         if (settingsDrawersOpen && app.settingsContent) {
            const drawerIndex = settingsDrawersOpen.findIndex(d => {
                return d.drawer === location.pathname
            });

            // If < 0, persistence does not exist yet
            if (drawerIndex < 0) {
                if (app.settingsLocation === location.pathname) {
                    setSettingsIcon(app.settingsIcon);
                    openRightDrawer();
                }
                else if (app.settingsLocation !== location.pathname) {
                    closeRightDrawer();
                }
            } else if (drawerIndex >= 0) {
                if (settingsDrawersOpen[drawerIndex].isOpen) {
                    openRightDrawer();
                } else {
                    closeRightDrawer();
                }
            }
        }
    }, [app.settingsContent, persist.settingsDrawersOpen]);

    // Set Avatar
    useEffect( () => {
        if (auth.authenticated) {
            dispatch(authGetAvatar());
        }
    }, [auth.authenticated]);

    // Get Toolbar height
    useEffect( () => {
        // The 'current' property contains info of the reference:
        // align, title, ... , width, height, etc.

        // Set initial Toolbar Height
        const initHeight = toolbar.current.offsetHeight;
        dispatch(appSetToolbarHeight(initHeight));

        function handleResize() {
            const height = toolbar.current.offsetHeight;
            dispatch(appSetToolbarHeight(height));
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    },[]);

    // Page Tile
    const pageTitle = t(app.pageTitle);

    // Component State
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const [settingsIcon, setSettingsIcon] = React.useState(<SettingsIcon/>);

    const login = () => {
         dispatch(authLogin());
    };

    const handleMenu = e => {
        // setAnchorEl(e.currentTarget);

        const modelConfig = {
            title: "Profile Menu",
            parent: { width: "408px" },
            paper: null,
            button: null,
            content: <ProfileMenu handleOnClose={handleClose} />,
            tooltipContent: null
        }
        dispatch(appSetModalContent(modelConfig));
    };

    const handleClose = e => {
        setAnchorEl(null);
    };

    const openLeftDrawer = e => {
        dispatch(appSetLeftDrawerOpen(true));
    };

    const closeLeftDrawer = e => {
        dispatch(appSetLeftDrawerOpen(false));
    };

    const openRightDrawer = e => {
        dispatch(appSetRightDrawerOpen(true));
        dispatch(persistSettingsDrawersOpen({drawer: location.pathname, isOpen: true}));
    };

    const closeRightDrawer = e => {
        dispatch(appSetRightDrawerOpen(false));
        dispatch(persistSettingsDrawersOpen({drawer: location.pathname, isOpen: false}));
    };

    // const closeDrawers = e => {
    //     closeLeftDrawer();
    //     closeRightDrawer();
    //     if (anchorEl) {
    //         handleClose();
    //     }
    // };
    //
    // const openDrawers = () => {
    //     openLeftDrawer();
    //     {app.settingsContent &&
    //         openRightDrawer();
    //     }
    // };

    /**
     * Set the background and color of the app bar
     * depends on the current selected theme.
     * @returns {Object} style - contains background and text color of the app bar.
     */
    const currentStyle = () => {
        return isThemeLight ? {
            background: "#f8f8f8",
            color: "#5c5c5c"
        } : {
            background: "#121212",
			backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))",
            color: "#fff"
        }
    }

    return (
        <StyledAppBar position="fixed" className={classes.appBar} style={currentStyle()}>
            <Toolbar ref={toolbar} variant="regular" style={{ height: "64px" }}>
				<React.Fragment>
					{!auth.authenticated || !user.authorized &&
						<IconButton
							disabled={true}
							color="inherit"
							aria-label=""
							onClick={closeLeftDrawer}
							edge="start"
							className={clsx(classes.menuButton, open && classes.hide)}
							size="large">
							<MenuIcon/>
						</IconButton>
					}
					{auth.authenticated && user.authorized &&
						<React.Fragment>
						{!app.leftDrawerOpen &&
							<IconButton
								color="inherit"
								aria-label="open drawer"
								onClick={openLeftDrawer}
								edge="start"
								className={clsx(classes.menuButton, open && classes.hide)}
								size="large">
							<MenuIcon/>
							</IconButton>
						}
						{app.leftDrawerOpen &&
							<IconButton
								color="inherit"
								aria-label="open drawer"
								onClick={closeLeftDrawer}
								edge="start"
								// onMouseLeave={closeLeftDrawer}
								className={clsx(classes.menuButton, open && classes.hide)}
								size="large">
							<MenuOpenIcon/>
							</IconButton>
						}
						</React.Fragment>
					}
					<img src={axiosBaseURL() + 'assets/logo.png'} alt="logo" style={{ maxHeight: '50px', paddingRight: '16px' }} />
					<Typography variant="h5" className={classes.title}>
						{config.title}
					</Typography>
					<div
						style={{
							width: '1px',
							height: '54px',
							backgroundColor: isThemeLight ? '#5c5c5c' : 'rgba(255,255,255,0.5)',
							marginLeft: '16px',
						}}
					/>
					<Typography variant="h6" className={classes.pageName}>
						{viewStaged &&
							<div>** STAGED DATA **</div>
						}
						{location.pathname === "/" ? "" : pageTitle}
					</Typography>
					<div className={classes.widgets}>
						{auth.authenticated && user.authorized &&
							<React.Fragment>
								{searchEnabled &&
								<div className={classes.searchBox}>
									<SearchBox searchConfig={searchConfig}/>
								</div>
								}
								<ClearAllFiltersBtn/>
							</React.Fragment>
						}
						{auth.authenticated && (
							<Typography className={`${classes.headline} xs-hidden`}>
								{homePageHeadline}
							</Typography>
						)}
						{auth.authenticated && user.authorized &&
							<React.Fragment>{app.settingsContent &&
									<React.Fragment>
										{!app.rightDrawerOpen &&
											<Tooltip title="Metrics Pane" arrow placement="bottom">
												<IconButton
													className={classes.settingsMenuButton}
													onClick={openRightDrawer}
													value="Closed"
													size="large">
													{settingsIcon}
												</IconButton>
											</Tooltip>
										}
										{app.rightDrawerOpen &&
											<Tooltip title="Metrics Pane" arrow placement="bottom">
												<IconButton
													className={classes.settingsMenuButton}
													onClick={closeRightDrawer}
													value="Open"
													selected={true}
													size="large">
													{settingsIcon}
												</IconButton>
											</Tooltip>
										}
									</React.Fragment>
								}
							</React.Fragment>
						}
						{!auth.authenticated && (
							<Button color="inherit" onClick={login}>
								Login
							</Button>
						)}
						{auth.authenticated && (
							<Tooltip title="Profile Menu" arrow placement="bottom">
								<IconButton
									className={classes.avatarButton}
									onClick={ () => {handleMenu()}}
									color="inherit"
									size="large">
									{auth.avatar && auth.avatar !== -2112 && (
										<Avatar className={classes.smallAvatar} src={auth.avatar} />
									)}
									{auth.avatar === -2112 && (
										<AccountCircle style={{ fontSize: '28px' }} />
									)}
								</IconButton>
							</Tooltip>
						)}
					</div>
				</React.Fragment>
            </Toolbar>
        </StyledAppBar>
    );
}
